import { useState, type FC, type MouseEvent } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useProductTourLauncher } from '@/components/ProductTour'
import { ProductTourIcon } from '@/components/ProductTour/ProductTourIcon'
import useTranslate from '@/hooks/use-translate'
import { TOUR_ANCHORS } from '@/lib/productTours/anchors'
import { SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX } from './side-nav-item'
import {
  SIDE_NAV_ITEM_FONT_SIZE,
  sideNavButtonHoverSx,
} from './side-nav-layout-dimensions'
import { densityPx, densityPxStr } from '@/theme/base/density'

type SideNavProductTourEntryProps = {
  isFolded: boolean
  showText: boolean
}

/**
 * « Visite guidée » : lance le tour de l'écran affiché (page et variante). Absente quand aucun
 * tour n'y est disponible ; plusieurs tours ouvrent un menu.
 */
export const SideNavProductTourEntry: FC<SideNavProductTourEntryProps> = ({
  isFolded,
  showText,
}) => {
  const { t } = useTranslate('productTour')
  const launcher = useProductTourLauncher()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  if (!launcher || launcher.pageTours.length === 0) return null
  const { pageTours, startTour } = launcher
  const label = t('launcher')

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (pageTours.length === 1) startTour(pageTours[0].id)
    else setMenuAnchor(event.currentTarget)
  }

  return (
    <>
      <Tooltip
        disableHoverListener={!isFolded}
        enterDelay={300}
        placement="right"
        title={label}
      >
        <Box sx={{ display: 'block', width: '100%' }}>
          <ButtonBase
            onClick={handleClick}
            aria-label={isFolded ? label : undefined}
            aria-haspopup={pageTours.length > 1 ? 'menu' : undefined}
            data-testid="side-nav-product-tour-entry"
            data-tour={TOUR_ANCHORS.sidenavTourButton}
            sx={{
              alignItems: 'center',
              borderLeft: `${densityPx(3)}px solid transparent`,
              borderRadius: 1,
              display: 'flex',
              gap: !isFolded ? 1 : 0,
              justifyContent: 'flex-start',
              pl: `${SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX - densityPx(3)}px`,
              pr: densityPxStr(16),
              height: densityPxStr(40),
              textAlign: 'left',
              width: '100%',
              backgroundColor: 'transparent',
              '&:hover': sideNavButtonHoverSx,
            }}
          >
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                color: 'var(--nav-item-icon-color)',
                display: 'inline-flex',
                flex: '0 0 auto',
                height: densityPxStr(28),
                justifyContent: 'center',
              }}
            >
              <ProductTourIcon fontSize="small" />
            </Box>
            {!isFolded && (
              <Typography
                sx={{
                  color: 'var(--nav-item-color)',
                  flexGrow: 1,
                  fontFamily: (theme) => theme.typography.fontFamily,
                  fontSize: SIDE_NAV_ITEM_FONT_SIZE,
                  fontWeight: 300,
                  lineHeight: densityPxStr(28),
                  minWidth: 0,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  marginLeft: densityPxStr(3),
                  opacity: showText ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: showText ? 'auto' : 'none',
                }}
              >
                {label}
              </Typography>
            )}
          </ButtonBase>
        </Box>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {pageTours.map((tour) => (
          <MenuItem
            key={tour.id}
            onClick={() => {
              setMenuAnchor(null)
              startTour(tour.id)
            }}
          >
            {tour.title}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
